import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import React from "react";
import styled, { css } from "styled-components";
export var FallbackOpacityAnimation = function(duration) {
    return css([
        "opacity:",
        ";transition:opacity ",
        " ease;"
    ], function(props) {
        return props.$isVisible ? 1 : 0;
    }, duration);
};
var INITIAL_VALUE = "initial";
var LOADING_BACKGROUND = "loading background";
var LOADED_CONTENT = "loaded content";
var FallbackHandlerContext = /*#__PURE__*/ React.createContext(INITIAL_VALUE);
export var createFallbackHandler = function(Comp, styles) {
    return /*#__PURE__*/ React.forwardRef(function(props, ref) {
        var asTag = props.asTag, isLoaded = props.isLoaded, backgroundTreatment = props.backgroundTreatment, loadedTreatment = props.loadedTreatment, children = props.children, rest = _object_without_properties(props, [
            "asTag",
            "isLoaded",
            "backgroundTreatment",
            "loadedTreatment",
            "children"
        ]);
        var _props_datatestid;
        var dataTestId = (_props_datatestid = props["data-test-id"]) !== null && _props_datatestid !== void 0 ? _props_datatestid : props.dataTestId;
        // Try to ignore IProps and only pass T to Wrapper, but somehow TS
        // complains about rest's type
        return /*#__PURE__*/ _jsx(Comp, _object_spread_props(_object_spread({}, rest), {
            as: asTag,
            ref: ref,
            children: /*#__PURE__*/ _jsx(FallbackHandlerContext.Consumer, {
                children: function(contextValue) {
                    if (contextValue === INITIAL_VALUE) {
                        // The main purpose of using a context here is to avoid duplicate nodes
                        // in the DOM tree. For example, for a product tile, when we create
                        // fallback at tile level and product name tile, what we want is:
                        //            tileWrapper                                     tileWrapper
                        //   tileFallBack   tileContent      NOT         tileFallBack             tileContent
                        // nameFallBack       nameContent          nameFallBack nameContent  nameFallBack nameContent
                        // which means each handler needs to know what content its in,
                        //   - if there is no parent handler, the handler the parent handler (tileWrapper)
                        //   - if there is a fallback handler above, the handler only needs to care about fallback (nameFallBack)
                        //   - if there is a content handler above, the handler only needs to care about content (nameContent)
                        return /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(FallbackHandlerContext.Provider, {
                                    value: LOADING_BACKGROUND,
                                    children: isPrerender() && isLoaded ? null : /*#__PURE__*/ _jsx("span", {
                                        style: {
                                            position: "relative"
                                        },
                                        children: /*#__PURE__*/ _jsx(FallbackParentWrapper, {
                                            "data-test-id": "handler-fallback-container".concat(dataTestId ? "-".concat(dataTestId) : ""),
                                            $isVisible: !isLoaded,
                                            "aria-hidden": isLoaded,
                                            $backgroundStyle: backgroundTreatment !== null && backgroundTreatment !== void 0 ? backgroundTreatment : styles === null || styles === void 0 ? void 0 : styles.backgroundStyle,
                                            children: children
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(FallbackHandlerContext.Provider, {
                                    value: LOADED_CONTENT,
                                    children: /*#__PURE__*/ _jsx(ContentWrapper, {
                                        "data-test-id": "handler-visible-container".concat(dataTestId ? "-".concat(dataTestId) : ""),
                                        $isVisible: isLoaded,
                                        "aria-hidden": !isLoaded,
                                        $otherStyle: loadedTreatment !== null && loadedTreatment !== void 0 ? loadedTreatment : styles === null || styles === void 0 ? void 0 : styles.loadedStyle,
                                        children: children
                                    })
                                })
                            ]
                        });
                    } else if (contextValue === LOADING_BACKGROUND) {
                        return /*#__PURE__*/ _jsx(FallbackChildWrapper, {
                            "data-test-id": "handler-fallback-container".concat(dataTestId ? "-".concat(dataTestId) : ""),
                            $isVisible: !isLoaded,
                            "aria-hidden": isLoaded,
                            $backgroundStyle: backgroundTreatment !== null && backgroundTreatment !== void 0 ? backgroundTreatment : styles === null || styles === void 0 ? void 0 : styles.backgroundStyle,
                            children: children
                        });
                    } else if (contextValue === LOADED_CONTENT) {
                        return /*#__PURE__*/ _jsx(ContentWrapper, {
                            "data-test-id": "handler-visible-container".concat(dataTestId ? "-".concat(dataTestId) : ""),
                            $isVisible: isLoaded,
                            "aria-hidden": !isLoaded,
                            $otherStyle: loadedTreatment !== null && loadedTreatment !== void 0 ? loadedTreatment : styles === null || styles === void 0 ? void 0 : styles.loadedStyle,
                            children: children
                        });
                    }
                    return null;
                }
            })
        }));
    });
};
var FallbackParentWrapper = styled.span.withConfig({
    componentId: "sc-e6cb3fa1-0"
})([
    "position:absolute;width:100%;height:100%;left:0;top:0;visibility:",
    ";",
    ";"
], function(props) {
    return props.$isVisible ? "visible" : "hidden";
}, function(props) {
    return props.$backgroundStyle;
});
var ContentWrapper = styled.span.withConfig({
    componentId: "sc-e6cb3fa1-1"
})([
    "visibility:",
    ";",
    ";"
], function(props) {
    return props.$isVisible ? "visible" : "hidden";
}, function(props) {
    return props.$otherStyle;
});
var FallbackChildWrapper = styled.span.withConfig({
    componentId: "sc-e6cb3fa1-2"
})([
    "visibility:",
    ";",
    ";"
], function(props) {
    return props.$isVisible ? "visible" : "hidden";
}, function(props) {
    return props.$backgroundStyle;
});
